import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import BadgeImage from './components/BadgeImage';
import BadgeSummary from './components/BadgeSummary';
import BadgeProgress from './components/BadgeProgress';
import BadgeDetails from './BadgeDetails';
import { withTranslation } from 'react-i18next';

// utils -- userSocialProfileImage
import profileImageHelperV2 from 'utils/userSocialProfileImage/profileImageHelperV2';
import 'utils/userSocialProfileImage/profileImageTypeDefsV2';

import styles from './AlternateBadge.module.css';

const AlternateBadge = (props) => {
    const {
        badge,
        viewer,
        viewerBadgeEarnedNumber,
        connection,
        connectionBadgeEarnedNumber,
        displayViewer,
        displayConnection,
        profileImageDtoMapState
    } = props;

    const { viewerProfileImageDtoSrc, viewerNoclip, connectionProfileImageDtoSrc, connectionNoclip } = useMemo(() => {
        const viewerDto = profileImageDtoMapState?.[viewer?.profileId];
        const connectionDto = profileImageDtoMapState?.[connection?.profileId];

        const viewerProfileImageDtoSrc = profileImageHelperV2.getViewerMediumFrameImage(viewerDto, viewer);
        const viewerNoclip = profileImageHelperV2.isNoclipProfile(viewerProfileImageDtoSrc);

        const connectionProfileImageDtoSrc = profileImageHelperV2.getDtoMediumFrameImage(connectionDto, connection);
        const connectionNoclip = profileImageHelperV2.isNoclipProfile(viewerProfileImageDtoSrc);

        return {
            viewerProfileImageDtoSrc,
            viewerNoclip,
            connectionProfileImageDtoSrc,
            connectionNoclip
        };
    }, [profileImageDtoMapState, viewer, connection]);

    return (
        <div className={`media ${props.available ? 'badge-available' : 'badge-earned'}`}>
            <div className="pull-left">
                <a className="badge-thumbnail" href="" onClick={(e) => onBadgeClicked(e, props)}>
                    <BadgeImage badge={badge} size="sml" />
                    {!(displayViewer || displayConnection) && badge.badgeProgressValue ? (
                        <BadgeProgress
                            badgeProgressValue={badge.badgeProgressValue}
                            badgeTargetValue={badge.badgeTargetValue}
                            badgeUnitId={badge.badgeUnitId}
                        />
                    ) : null}
                </a>
            </div>
            <div className="media-body">
                <a className="badge-summary" href="" onClick={(e) => onBadgeClicked(e, props)}>
                    <BadgeSummary {...props} listStyle />
                </a>
                {displayViewer || displayConnection ? (
                    <div className="badge-compare">
                        {displayViewer ? (
                            <div className="badge-compare-user">
                                <button
                                    className={styles.avatarWrapper}
                                    type="button"
                                    onClick={(e) => onBadgeClicked(e, props)}
                                >
                                    <img
                                        className="avatar avatar-xsmall"
                                        alt={viewer.fullName || viewer.displayName}
                                        src={viewerProfileImageDtoSrc}
                                        data-noclip={viewerNoclip}
                                        onError={(evt) => {
                                            evt.currentTarget.src = profileImageHelperV2.getDefaultProfileImage();
                                            delete evt.currentTarget.dataset.noclip;
                                        }}
                                    />
                                    {viewerBadgeEarnedNumber > 1 ? (
                                        <span className="badge-repeatable">{viewerBadgeEarnedNumber}</span>
                                    ) : null}
                                </button>
                            </div>
                        ) : null}
                        {displayConnection ? (
                            <div className="badge-compare-connection">
                                <button
                                    className={styles.avatarWrapper}
                                    type="button"
                                    onClick={(e) => onBadgeClicked(e, props, connection.displayName)}
                                >
                                    <img
                                        className="avatar avatar-xsmall"
                                        alt={connection.fullName || connection.displayName}
                                        src={connectionProfileImageDtoSrc}
                                        data-noclip={connectionNoclip}
                                        onError={(evt) => {
                                            evt.currentTarget.src = profileImageHelperV2.getDefaultProfileImage();
                                            delete evt.currentTarget.dataset.noclip;
                                        }}
                                    />
                                    {connectionBadgeEarnedNumber > 1 ? (
                                        <span className="badge-repeatable">{connectionBadgeEarnedNumber}</span>
                                    ) : null}
                                </button>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

const onBadgeClicked = (e, props, displayName) => {
    e.preventDefault();

    const { showModal, displayViewer } = props;
    let view = <BadgeDetails {...props} />;

    if (!displayViewer) {
        view = <BadgeDetails {...props} displayName={null} />;
    }

    if (displayName) {
        view = <BadgeDetails {...props} displayName={displayName} />;
    }

    showModal({
        view,
        title: 'badges:badge_detail'
    });
};

AlternateBadge.propTypes = {
    badge: PropTypes.object.isRequired,
    badgesAttributes: PropTypes.object.isRequired,
    viewerSocialProfile: PropTypes.object.isRequired,
    showModal: PropTypes.func.isRequired,
    available: PropTypes.bool
};

export default withTranslation(['badges'])(AlternateBadge);
